import React, { memo, FC, useState } from 'react';
import { getFormattedDate, isMobile } from './utils';
import getContentUnitImageRatio from '../../js/utils/getContentUnitImageRatio';
import Author from './components/author';
import CardExpanded from './components/card-expanded';
import CardExpandedVideo from './components/card-expanded-video';
import { trackClick } from './tracking';
import { CardTypes } from './utils';

const {
  wp: {
    i18n: {
      __,
    },
  },
} = window;

interface CardProps {
  card: CardTypes;
  expired: string;
  marketTimezone: string;
}

const Card: FC<CardProps> = ({
  card: {
    date_time: dateTime,
    bylines = [],
    title,
    type,
    network_object_id: networkObjectId,
    is_pinned: isPinned,
    hide_title: hideTitle,
    share_links: {
      facebook,
      twitter,
      email,
      native,
    },
  },
  card,
  expired,
  marketTimezone,
}) => {
  const imageUrl = String(getContentUnitImageRatio(card, '16:9', 850));
  const bylineTranslation = __('and', 'nbc');
  const { agoTime, timeFormatted, tidyDate } = getFormattedDate(dateTime, marketTimezone);
  const pinnedClass = isPinned ? ' liveblog-card__pinned' : '';
  const [showShare, setShowShare] = useState(false);

  // #region Share functionality
  const socialButtons = [
    {
      shareTypeClass: 'facebook-f',
      buttonType: 'facebook',
      shareUrl: facebook,
    },
    {
      shareTypeClass: 'twitter-x',
      buttonType: 'twitter',
      shareUrl: twitter,
    },
    {
      shareTypeClass: 'envelope',
      buttonType: 'email',
      shareUrl: email,
    },
    {
      shareTypeClass: 'link',
      buttonType: 'link',
      shareUrl: decodeURIComponent(native),
    },
  ];

  const shareData = {
    title,
    url: decodeURIComponent(native),
  };

  const nativeShare = (async () => {
    await navigator.share(shareData);
  });

  const handleShare = () => {
    if (showShare) {
      trackClick('share: close button', 'click to share', networkObjectId, title);
      setShowShare(false);
      return;
    }

    trackClick('share: share button', 'click to share', networkObjectId, title);

    if (isMobile && navigator.canShare?.(shareData)) { // checks to see if navigator.share is supported
      nativeShare();
    } else {
      setShowShare(true);
    }
  };

  const doLinkShare = (event, url) => {
    event.preventDefault();
    window.navigator?.clipboard?.writeText?.(url);
    setShowShare(false);
  };

  return (
    <div className={`liveblog-card liveblog-card__${type}${pinnedClass}`} data-card-id={networkObjectId} data-card-title={title}>
      <div className="liveblog-card__header">
        <div className="liveblog-card__meta">
          <span className={`liveblog-card__time liveblog-card__time--${expired ? 'expired' : 'live'}`}>
            {expired ? tidyDate : agoTime}<span>{timeFormatted}</span>
          </span>
          <div className={`liveblog-card__share share-buttons ${showShare && 'liveblog-card__share--show'}`}>
            {
              socialButtons.map(({ shareTypeClass, buttonType, shareUrl }) => (
                <a
                  href={'link' !== buttonType ? shareUrl : ''}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(event) => {
                    trackClick(`share: ${buttonType}`, 'click to share', networkObjectId, title);
                    if ('link' === buttonType) {
                      doLinkShare(event, shareUrl);
                    }
                  }}
                >
                  <span className={`icon icon-${shareTypeClass}`} />
                </a>
              ))}
          </div>
          <button
            className={`liveblog-card__share-icon ${showShare && 'liveblog-card__share-icon--close'}`}
            onClick={handleShare}
            type="button"
            aria-label="share button"
          />
        </div>
        {! hideTitle &&
          <h2 className="liveblog-card__title">{title}</h2>
        }
        {0 < bylines.length &&
          <h4 className="liveblog-card__byline">{__('By', 'nbc')} {''}
            {
              bylines.slice(0, 3).map(({ display_name: author, url, rsn_is_insider: isInsider, rsn_insider_team: insiderTeam }, index, authors) => {
                if (index + 1 === authors.length && 1 !== authors.length) {
                  return <> {bylineTranslation}  <Author name={author} url={url} isInsider={isInsider} insiderTeam={insiderTeam || ''} /></>;
                } else if (0 !== index) {
                  return <>,  <Author name={author} url={url} isInsider={isInsider} insiderTeam={insiderTeam || ''} /></>;
                }
                return <Author name={author} url={url} isInsider={isInsider} insiderTeam={insiderTeam || ''} />;
              })
            }
          </h4>
        }
      </div>
      {('post' === type || 'card' === type) && (
        <CardExpanded
          card={card}
          key={networkObjectId}
          imageUrl={imageUrl}
        />
      )}

      {'video' === type && (
        <CardExpandedVideo
          card={card}
          key={networkObjectId}
        />
      )}
    </div>
  );
};

export default memo(Card);
